import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Download, Loader2, LogOut, Copy } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "dualhub_admin_token";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LoginView({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: password.trim() });
      const token = res?.data?.token;
      if (token) {
        sessionStorage.setItem(TOKEN_KEY, token);
        onSuccess(token);
        toast.success("Welcome in.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6 bg-[#050505]"
      data-testid="admin-login-view"
    >
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
        data-testid="admin-login-form"
      >
        <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#c48a64]">
          / Admin — Inner Circle
        </p>
        <h1 className="mt-6 font-display text-4xl md:text-5xl leading-[0.98] font-semibold text-[#f5f5f5]">
          Access the <span className="italic font-medium">list.</span>
        </h1>
        <p className="mt-4 text-[#a3a3a3] leading-relaxed">
          Enter the admin password to view Inner Circle sign-ups and export the CSV.
        </p>

        <label className="block mt-10">
          <span className="font-mono-alt text-[10px] tracking-[0.28em] uppercase text-[#6b6b6b]">
            Password
          </span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
            disabled={loading}
            className="input-line mt-3"
            data-testid="admin-password-input"
            autoComplete="current-password"
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="btn-copper group mt-10 inline-flex items-center justify-between gap-6 px-6 py-4 text-[13px] font-mono-alt tracking-[0.18em] uppercase w-full disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? "Signing in…" : "Unlock Admin"}
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <ArrowUpRight
              size={18}
              className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
            />
          )}
        </button>

        <a
          href="/"
          className="mt-8 inline-block font-mono-alt text-[11px] tracking-[0.24em] uppercase text-[#6b6b6b] copper-underline"
          data-testid="admin-back-home"
        >
          ← Back to site
        </a>
      </motion.form>
    </div>
  );
}

function LeadsView({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, {
        headers: { "X-Admin-Password": token },
      });
      setLeads(res.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        onLogout();
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/leads/export`, {
        headers: { "X-Admin-Password": token },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dual-hub-leads-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${leads.length} lead${leads.length === 1 ? "" : "s"}.`);
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const copyEmails = async () => {
    if (!leads.length) return;
    const text = leads.map((l) => l.email).join(", ");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("All emails copied to clipboard.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    onLogout();
    toast.success("Signed out.");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f5f5]" data-testid="admin-leads-view">
      <header className="border-b border-[#262626] sticky top-0 z-30 backdrop-blur-md bg-[#050505]/80">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 h-16 flex items-center justify-between">
          <a
            href="/"
            className="font-mono-alt text-[11px] tracking-[0.28em] uppercase text-[#f5f5f5]"
            data-testid="admin-header-logo"
          >
            <span className="text-[#c48a64]">◆</span> The Dual Hub · Admin
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 font-mono-alt text-[11px] tracking-[0.24em] uppercase text-[#a3a3a3] copper-underline"
          >
            <LogOut size={14} />
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 md:px-10 py-14 md:py-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="font-mono-alt text-[11px] tracking-[0.3em] uppercase text-[#c48a64]">
              / Inner Circle · Leads
            </p>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] font-semibold">
              {loading ? "…" : leads.length}{" "}
              <span className="text-[#a3a3a3] italic font-medium text-3xl sm:text-4xl md:text-5xl">
                {leads.length === 1 ? "subscriber" : "subscribers"}
              </span>
            </h1>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={copyEmails}
              disabled={!leads.length}
              data-testid="admin-copy-emails"
              className="btn-ghost inline-flex items-center gap-3 px-5 py-3 text-[12px] font-mono-alt tracking-[0.18em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Copy size={14} />
              Copy emails
            </button>
            <button
              onClick={downloadCsv}
              disabled={exporting || !leads.length}
              data-testid="admin-export-csv"
              className="btn-copper group inline-flex items-center gap-3 px-5 py-3 text-[12px] font-mono-alt tracking-[0.18em] uppercase disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        <div className="border border-[#262626] overflow-hidden" data-testid="admin-leads-table-wrap">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#262626] font-mono-alt text-[10px] tracking-[0.28em] uppercase text-[#6b6b6b] bg-[#0a0a0a]">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Name</div>
            <div className="col-span-5">Email</div>
            <div className="col-span-3">Joined</div>
          </div>

          {loading ? (
            <div className="px-6 py-16 flex items-center justify-center text-[#a3a3a3]" data-testid="admin-leads-loading">
              <Loader2 size={18} className="animate-spin mr-3" />
              Loading leads…
            </div>
          ) : leads.length === 0 ? (
            <div className="px-6 py-20 text-center" data-testid="admin-leads-empty">
              <p className="font-display text-2xl md:text-3xl text-[#f5f5f5]">No sign-ups yet.</p>
              <p className="mt-3 text-[#a3a3a3]">
                Sign-ups from the Inner Circle form will appear here in real time.
              </p>
            </div>
          ) : (
            <ul>
              {leads.map((l, i) => (
                <li
                  key={l.id}
                  data-testid={`admin-lead-row-${i}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-[#1a1a1a] last:border-b-0 hover:bg-[#0a0a0a] transition-colors duration-200"
                >
                  <div className="md:col-span-1 font-mono-alt text-[11px] tracking-[0.2em] text-[#c48a64]">
                    {String(i + 1).padStart(3, "0")}
                  </div>
                  <div className="md:col-span-3 text-[#f5f5f5]">{l.name}</div>
                  <div className="md:col-span-5 text-[#a3a3a3] font-mono-alt text-[13px] break-all">
                    {l.email}
                  </div>
                  <div className="md:col-span-3 text-[#a3a3a3] text-sm">
                    {formatDate(l.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}

export default function AdminLeads() {
  const [token, setToken] = useState(() =>
    typeof window !== "undefined" ? sessionStorage.getItem(TOKEN_KEY) : null
  );

  if (!token) {
    return <LoginView onSuccess={setToken} />;
  }
  return <LeadsView token={token} onLogout={() => setToken(null)} />;
}
